@if (cookieService.showBanner()) {
  <!-- Backdrop Blur Overlay when detailed preferences modal is open -->
  @if (cookieService.showCustomizeModal()) {
    <button
      type="button"
      class="fixed inset-0 z-40 bg-slate-a8 backdrop-blur-sm transition-opacity w-full h-full border-none cursor-default p-0"
      (click)="cookieService.closeCustomizeModal()"
      aria-label="Fermer les détails"
    ></button>
  }

  <!-- Main Floating Banner / Container -->
  <div
    class="fixed bottom-4 right-4 left-4 sm:left-auto sm:max-w-lg z-50 rounded-2xl border border-slate-6 bg-slate-1/95 backdrop-blur-xl p-5 shadow-2xl transition-all duration-300 animate-in fade-in slide-in-from-bottom-6"
    role="dialog"
    aria-labelledby="cookie-consent-title"
  >
    <!-- Header -->
    <div class="flex items-start justify-between gap-3 mb-3">
      <div class="flex items-center gap-2.5">
        <div class="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-amber-a3 text-amber-11">
          <mat-icon>cookie</mat-icon>
        </div>
        <div>
          <h2 id="cookie-consent-title" class="text-base font-semibold text-slate-12 leading-snug">
            Gestion des cookies et confidentialité
          </h2>
          <span class="text-xs text-slate-11">Conformité RGPD / CNIL</span>
        </div>
      </div>

      @if (cookieService.consentGiven()) {
        <button matIconButton type="button" (click)="cookieService.closeCustomizeModal()" aria-label="Fermer">
          <mat-icon>close</mat-icon>
        </button>
      }
    </div>

    <!-- Quick Overview View -->
    @if (!cookieService.showCustomizeModal()) {
      <p class="text-xs sm:text-sm text-slate-11 leading-relaxed mb-4">
        Nous utilisons des cookies essentiels pour assurer le bon fonctionnement du site. Avec votre
        consentement, nous utilisons également des cookies analytiques (Google Analytics) pour mesurer
        l'audience et améliorer votre expérience.
      </p>

      <div class="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-2">
        <button matButton type="button" class="link" (click)="onOpenCustomize()">
          <mat-icon>tune</mat-icon>
          <span>Personnaliser</span>
        </button>

        <div class="flex items-center gap-2 justify-end">
          <button matButton type="button" class="secondary" (click)="cookieService.declineAll()">
            <span>Refuser</span>
          </button>
          <button matButton type="button" class="primary" (click)="cookieService.acceptAll()">
            <span>Tout accepter</span>
          </button>
        </div>
      </div>
    }

    <!-- Detailed Customization Modal View -->
    @if (cookieService.showCustomizeModal()) {
      <div class="space-y-3 my-4 max-h-[320px] overflow-y-auto pr-1">
        <!-- Essential Cookies -->
        <div class="p-3 rounded-xl bg-slate-2 border border-slate-4">
          <div class="flex items-center justify-between gap-2 mb-1">
            <span class="text-xs font-semibold text-slate-12 flex items-center gap-1.5">
              <mat-icon class="!w-4 !h-4 !text-[16px] text-green-10">check_circle</mat-icon>
              Cookies essentiels
            </span>
            <span class="text-[10px] font-medium text-green-11 bg-green-a3 px-2 py-0.5 rounded-full">
              Toujours actifs
            </span>
          </div>
          <p class="text-[11px] text-slate-11 leading-normal">
            Indispensables pour la sécurité, l'authentification et le choix de vos préférences (ex: thème
            sombre/clair).
          </p>
        </div>

        <!-- Analytics Cookies -->
        <div class="p-3 rounded-xl bg-slate-2 border border-slate-4">
          <div class="flex items-center justify-between gap-2 mb-1">
            <span class="text-xs font-semibold text-slate-12 flex items-center gap-1.5">
              <mat-icon class="!w-4 !h-4 !text-[16px] text-blue-10">analytics</mat-icon>
              Cookies analytiques (Google Analytics)
            </span>
            <button
              type="button"
              (click)="onToggleAnalytics()"
              [class]="
                'relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ' +
                (tempAnalytics() ? 'bg-blue-9' : 'bg-slate-6')
              "
              role="switch"
              [aria-checked]="tempAnalytics()"
            >
              <span
                [class]="
                  'pointer-events-none inline-block h-4 w-4 transform rounded-full bg-slate-1 shadow-lg ring-0 transition duration-200 ease-in-out ' +
                  (tempAnalytics() ? 'translate-x-4' : 'translate-x-0')
                "
              ></span>
            </button>
          </div>
          <p class="text-[11px] text-slate-11 leading-normal">
            Permettent de mesurer le nombre de visites, de comprendre comment vous naviguez et d'optimiser
            l'ergonomie du site.
          </p>
        </div>

        <!-- Marketing Cookies -->
        <div class="p-3 rounded-xl bg-slate-2 border border-slate-4">
          <div class="flex items-center justify-between gap-2 mb-1">
            <span class="text-xs font-semibold text-slate-12 flex items-center gap-1.5">
              <mat-icon class="!w-4 !h-4 !text-[16px] text-purple-10">campaign</mat-icon>
              Cookies marketing et ciblage
            </span>
            <button
              type="button"
              (click)="onToggleMarketing()"
              [class]="
                'relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ' +
                (tempMarketing() ? 'bg-purple-9' : 'bg-slate-6')
              "
              role="switch"
              [aria-checked]="tempMarketing()"
            >
              <span
                [class]="
                  'pointer-events-none inline-block h-4 w-4 transform rounded-full bg-slate-1 shadow-lg ring-0 transition duration-200 ease-in-out ' +
                  (tempMarketing() ? 'translate-x-4' : 'translate-x-0')
                "
              ></span>
            </button>
          </div>
          <p class="text-[11px] text-slate-11 leading-normal">
            Utilisés pour mesurer l'efficacité de nos campagnes publicitaires et vous proposer du contenu
            pertinent.
          </p>
        </div>
      </div>

      <!-- Footer Action Buttons inside Detailed View -->
      <div class="flex items-center justify-between gap-2 pt-2 border-t border-slate-4">
        <a [routerLink]="privacyPolicyUrl()" class="text-[11px] text-slate-11 hover:text-slate-12 underline">
          Politique de confidentialité
        </a>

        <div class="flex items-center gap-2">
          <button matButton type="button" class="secondary" (click)="cookieService.closeCustomizeModal()">
            <span>Annuler</span>
          </button>
          <button matButton type="button" class="primary" (click)="onSaveCustomChoices()">
            <span>Enregistrer mes choix</span>
          </button>
        </div>
      </div>
    }
  </div>
}
